import styled from 'styled-components';
import Logo from '@/components/Menu/shared/Logo';
import { getCssSizeValue } from '@repo/utils';
import { BREAKPOINTS } from '@repo/ui/themeSettings';
import { useMemo } from 'react';
import { CURSOR_TYPES } from '@repo/utils/constants';
import LanguageChange from '@/components/Menu/shared/LanguageChange';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import useSpecifyColor from '@repo/ui/src/hooks/useSpecifyColor';
import { useIsMobile } from '@repo/ui/src/providers/DeviceProvider';
import LetsTalkButton from '@repo/ui/src/components/common/LetsTalkButton';
import { useScrollContext } from '@repo/ui/src/components/Scroll';
import useHideTopBarOnScroll from '@repo/ui/src/hooks/useHideTopBarOnScroll';
import useSetIsMenuTransparentOnPageLoad from '@repo/ui/src/hooks/useSetIsMenuTransparentOnPageLoad';
import Button from '@repo/ui/src/components/common/Button';
import Link from '@repo/ui/src/components/common/Link';

const StyledTopRow = styled.div`
	z-index: 99;
	align-items: center;
	display: flex;
	justify-content: space-between;
	width: 100%;
	padding-top: ${getCssSizeValue(15, BREAKPOINTS.MOBILE)};
	padding-bottom: ${getCssSizeValue(15, BREAKPOINTS.MOBILE)};
	position: fixed;
	transition: all var(--color-transition-duration) var(--ease);
	transform: translateY(-100%);
	will-change: transform, opacity;

	&.menu-small {
		padding-top: ${getCssSizeValue(10, BREAKPOINTS.MOBILE)};
		padding-bottom: ${getCssSizeValue(10, BREAKPOINTS.MOBILE)};
	}

	&.open {
		background: transparent;
	}

	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet}px) {
		padding-top: ${getCssSizeValue(25, BREAKPOINTS.TABLET)};
		padding-bottom: ${getCssSizeValue(15, BREAKPOINTS.TABLET)};

		&.menu-small {
			padding-top: ${getCssSizeValue(10, BREAKPOINTS.TABLET)};
			padding-bottom: ${getCssSizeValue(10, BREAKPOINTS.TABLET)};
		}
	}

	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		padding-top: ${getCssSizeValue(45, BREAKPOINTS.DESKTOP)};
		padding-bottom: ${getCssSizeValue(10, BREAKPOINTS.DESKTOP)};

		&.menu-small {
			padding-top: ${getCssSizeValue(10, BREAKPOINTS.DESKTOP)};
			padding-bottom: ${getCssSizeValue(10, BREAKPOINTS.DESKTOP)};
		}
	}
`;

const StyledTopRowBackground = styled.div`
	position: absolute;
	z-index: 1;
	inset: 0;
	transition: all var(--color-transition-duration) var(--ease);
	background: var(--primary-color);
	opacity: 1;
	will-change: opacity, background;

	&.transparent {
		opacity: 0;
	}
`;

const StyledRightPartWrapper = styled.div`
	position: relative;
	z-index: 10;
	display: flex;
	align-items: center;
	gap: ${getCssSizeValue(20, BREAKPOINTS.DESKTOP)};
	margin-right: ${getCssSizeValue(10, BREAKPOINTS.MOBILE)};

	@media (min-width: ${({ theme }) => theme?.breakpoints?.desktop || 1024}px) and (orientation: landscape) {
		margin-right: ${getCssSizeValue(10, BREAKPOINTS.DESKTOP)};
	}
`;

const StyledLogoWrapper = styled.div`
	z-index: 1000;
`;

const StyledSingleNavItem = styled(Button)`
	text-transform: lowercase;
	font-weight: ${(props) => props.theme.fontWeight.semiBold};
	color: var(--secondary-color);
`;

const TopRow = ({ firstMenuItem, contactButtonText, contactButtonLink, menuItems = [] }) => {
	const bgColor = useSpecifyColor();
	const { isMenuTransparent } = useSelector((state) => state.menu);
	const contactButtonLinkSlug = contactButtonLink ? `/${contactButtonLink?.slug}` : '';
	const { asPath } = useRouter();
	const normalizedPath = useMemo(() => (asPath === '/' ? 'home' : asPath), [asPath]);
	const router = useRouter();
	const {
		instance: { __lenis },
	} = useScrollContext();
	const isMobile = useIsMobile();

	const handleContactClick = () => {
		if (router.asPath.includes('contact')) {
			__lenis.scrollTo(0);
		} else {
			router.push(contactButtonLinkSlug, contactButtonLinkSlug, { scroll: false });
		}
	};

	const { isMenuSmall } = useHideTopBarOnScroll();
	useSetIsMenuTransparentOnPageLoad();

	return (
		<StyledTopRow
			data-cursor={CURSOR_TYPES.HTML}
			$bgColor={bgColor}
			id="menu-top-row"
			className={`menu-top-row px-main ${isMenuSmall ? 'menu-small' : ''}`}
		>
			<StyledLogoWrapper>
				<Logo firstMenuItem={firstMenuItem} />
			</StyledLogoWrapper>
			<StyledTopRowBackground className={`top-row-background ${isMenuTransparent ? 'transparent' : ''}`} />
			<StyledRightPartWrapper>
				{menuItems.map((item) => {
					const id = item?.id || '';
					const text = item?.link?.menuContext?.previewText || '';
					const slug = item?.link?.slug || '';
					const isSlugHome = slug === 'home';
					const href = isSlugHome ? '/' : `/${slug}`;
					const isActiveRoute = normalizedPath.includes(slug);
					const isPleoGroupOptionHiddenOnMobile = isSlugHome && isMobile;

					return (
						!isPleoGroupOptionHiddenOnMobile && (
							<Link href={href} key={id}>
								<StyledSingleNavItem
									variant="unstyled"
									key={id}
									data-cursor={CURSOR_TYPES.LINK}
									className={`underline-animation ${isActiveRoute ? 'active' : ''}`}
								>
									{text}
								</StyledSingleNavItem>
							</Link>
						)
					);
				})}
				<LetsTalkButton onClick={handleContactClick} pleo="group">
					{contactButtonText}
				</LetsTalkButton>
				<LanguageChange />
			</StyledRightPartWrapper>
		</StyledTopRow>
	);
};

export default TopRow;
